import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { cn } from '@shared/lib/cn';
import { sendLiveChatMessage, type LiveChatMessage } from './liveChatApi';

/** Sits above the fixed TabBar + safe area (mirrors SupportDetail). */
const TAB_BAR_OFFSET = 'calc(4.25rem + env(safe-area-inset-bottom, 0px))';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  suggestTicket?: boolean;
}

const GREETING: ChatMessage = {
  id: 0,
  role: 'assistant',
  content:
    "Hi! 👋 I'm your community assistant. Ask me about bookings, billing, maintenance, visitor access — anything estate-related. I'll help or log it for the team.",
};

/**
 * Live Chat — a simulated AI assistant for the support module. The reply engine
 * (`sendLiveChatMessage`) is a stand-in to be swapped for the real AI backend.
 */
export function SupportLiveChat(): JSX.Element {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [typing, setTyping] = useState(false);
  const [body, setBody] = useState('');
  const idRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  const send = async () => {
    const text = body.trim();
    if (!text || typing) return;
    setBody('');
    const userMsg: ChatMessage = { id: idRef.current++, role: 'user', content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setTyping(true);
    try {
      const history: LiveChatMessage[] = next.map((m) => ({ role: m.role, content: m.content }));
      const reply = await sendLiveChatMessage(history);
      setMessages((prev) => [
        ...prev,
        { id: idRef.current++, role: 'assistant', content: reply.content, suggestTicket: reply.suggestTicket },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: idRef.current++,
          role: 'assistant',
          content: "Sorry — I couldn't reach the assistant just now. You can still log a support request and the team will help.",
          suggestTicket: true,
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <div
      className="-mb-24 flex flex-col overflow-hidden bg-paper"
      style={{ height: '100dvh', paddingBottom: TAB_BAR_OFFSET }}
    >
      <HeroHeader
        className="shrink-0"
        onBack={() => navigate('/app/support/requests')}
        backLabel="Back to support"
        eyebrow="Support"
        eyebrowSuffix={
          <span className="inline-flex items-center gap-1 rounded-pill bg-white/15 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-white/90">
            <Sparkles className="h-3 w-3" strokeWidth={2} />
            AI
          </span>
        }
        title="Live Chat"
        subtitle="Chat with our community assistant"
        titleClassName="mt-2 leading-tight"
      />

      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-5 pt-4 pb-4"
      >
        <p className="mx-auto mb-4 w-fit rounded-pill bg-sunken px-3 py-1 text-center text-caption text-ink-muted">
          AI assistant — replies are simulated for now
        </p>

        <section className="flex min-w-0 flex-col gap-3">
          {messages.map((msg) => (
            <Bubble
              key={msg.id}
              msg={msg}
              onLogTicket={() => navigate('/app/support/requests/new')}
            />
          ))}
          {typing ? <TypingBubble /> : null}
        </section>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        className="shrink-0 border-t border-[color:var(--hairline)] bg-paper px-5 pt-3 pb-3 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Ask the assistant…"
            rows={1}
            enterKeyHint="send"
            className={cn(
              'min-h-11 max-h-32 flex-1 resize-none rounded-2xl bg-[color:var(--surface-sunken)]',
              'px-4 py-2.5 text-body leading-5 text-ink',
              'border border-transparent focus:border-primary-500 focus:bg-card focus:outline-none',
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button
            type="submit"
            size="icon"
            className="h-11 w-11 shrink-0"
            disabled={!body.trim() || typing}
            aria-label="Send message"
          >
            <Send className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </form>
    </div>
  );
}

function Bubble({ msg, onLogTicket }: { msg: ChatMessage; onLogTicket: () => void }): JSX.Element {
  const isUser = msg.role === 'user';
  return (
    <article className={cn('flex min-w-0 flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[88%] min-w-0 whitespace-pre-line break-words rounded-2xl px-4 py-3 text-body leading-relaxed',
          isUser
            ? 'rounded-br-md bg-gradient-to-br from-[var(--primary-600)] via-[var(--primary-700)] to-[var(--primary-800)] text-white shadow-sm'
            : 'rounded-bl-md bg-[color:var(--surface-sunken)] text-ink',
        )}
      >
        {msg.content}
      </div>
      {!isUser && msg.suggestTicket ? (
        <button
          type="button"
          onClick={onLogTicket}
          className="rounded-pill bg-primary-50 px-3 py-1.5 text-caption font-semibold text-primary-700"
        >
          Log a support request
        </button>
      ) : null}
    </article>
  );
}

function TypingBubble(): JSX.Element {
  return (
    <article className="flex items-start">
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-[color:var(--surface-sunken)] px-4 py-3.5">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1.5 w-1.5 animate-bounce rounded-pill bg-ink-muted"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </article>
  );
}
